import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ban, Plus, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function StatusBadge({ doc }) {
  const now = new Date().toISOString();
  if (doc.used) return <span className="oc-status-fair text-[11px] uppercase tracking-wide font-semibold px-2 py-1 rounded-md">Gebruikt</span>;
  if (doc.revoked) return <span className="bg-oc-textSecondary/20 text-oc-textSecondary text-[11px] uppercase tracking-wide font-semibold px-2 py-1 rounded-md">Ingetrokken</span>;
  if (doc.valid_until_iso && doc.valid_until_iso < now)
    return <span className="oc-status-excessive text-[11px] uppercase tracking-wide font-semibold px-2 py-1 rounded-md">Verlopen</span>;
  return <span className="bg-oc-secondary/10 text-oc-secondary text-[11px] uppercase tracking-wide font-semibold px-2 py-1 rounded-md">Actief</span>;
}

export default function AdminDiscountCodes() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPercent, setNewPercent] = useState(20);
  const [newDays, setNewDays] = useState(90);
  const [newNote, setNewNote] = useState("");

  const load = async () => {
    const params = new URLSearchParams({ skip: 0, limit: 200 });
    if (status) params.append("status", status);
    if (emailFilter) params.append("email", emailFilter);
    const { data } = await api.get(`/discount-codes/admin/list?${params.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
  };

  useEffect(() => {
    load();
  }, [status, emailFilter]);

  const create = async () => {
    if (!newEmail.trim()) {
      toast.error("E-mail is vereist");
      return;
    }
    try {
      const { data } = await api.post("/discount-codes/admin/generate", {
        email: newEmail.trim(),
        percent_off: Number(newPercent),
        valid_days: Number(newDays),
        note: newNote || undefined,
      });
      toast.success(`Code ${data.code} aangemaakt`);
      setShowCreate(false);
      setNewEmail("");
      setNewNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmaken mislukt");
    }
  };

  const revoke = async (code) => {
    if (!confirm(`Code ${code} intrekken? De klant kan deze niet meer gebruiken.`)) return;
    try {
      await api.post(`/discount-codes/admin/${code}/revoke`);
      toast.success("Code ingetrokken");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Intrekken mislukt");
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} gekopieerd`);
  };

  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-semibold text-oc-primary text-[34px] leading-tight">Kortingscodes</h1>
          <p className="text-oc-textSecondary text-[14px]">{total} codes · refer-a-friend + admin-uitgegeven</p>
        </div>
        <Button
          data-testid="create-code-btn"
          onClick={() => setShowCreate((v) => !v)}
          className="rounded-oc-btn bg-oc-primary hover:bg-oc-secondary text-white px-5"
        >
          <Plus className="w-4 h-4 mr-2" /> Nieuwe code
        </Button>
      </div>

      {showCreate && (
        <div className="bg-white border border-oc-border rounded-oc p-7 space-y-4">
          <h2 className="font-display font-semibold text-oc-primary text-[18px]">Code aanmaken voor klant</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>E-mail klant</Label>
              <Input data-testid="new-code-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="h-12 rounded-oc-btn" />
            </div>
            <div className="space-y-2">
              <Label>Notitie (optioneel)</Label>
              <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} className="h-12 rounded-oc-btn" placeholder="bv. Goodwill na klacht" />
            </div>
            <div className="space-y-2">
              <Label>Korting %</Label>
              <Input type="number" min="1" max="99" value={newPercent} onChange={(e) => setNewPercent(Number(e.target.value) || 20)} className="h-12 rounded-oc-btn font-mono" />
            </div>
            <div className="space-y-2">
              <Label>Geldig (dagen)</Label>
              <Input type="number" min="1" max="730" value={newDays} onChange={(e) => setNewDays(Number(e.target.value) || 90)} className="h-12 rounded-oc-btn font-mono" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="confirm-create-code" onClick={create} className="rounded-oc-btn bg-oc-primary hover:bg-oc-secondary text-white">Aanmaken</Button>
            <Button onClick={() => setShowCreate(false)} variant="ghost" className="rounded-oc-btn">Annuleren</Button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          placeholder="Filter op e-mail..."
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          className="rounded-oc-btn"
          data-testid="filter-email"
        />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-oc-btn" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="active">Actief</SelectItem>
            <SelectItem value="used">Gebruikt</SelectItem>
            <SelectItem value="expired">Verlopen</SelectItem>
            <SelectItem value="revoked">Ingetrokken</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-oc-border rounded-oc overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 bg-oc-subtle/60 text-[11px] uppercase tracking-wide font-semibold text-oc-textSecondary">
          <span className="col-span-3">Code</span>
          <span className="col-span-3">E-mail</span>
          <span className="col-span-1">%</span>
          <span className="col-span-2">Geldig tot</span>
          <span className="col-span-2">Status / bron</span>
          <span className="col-span-1 text-right">Acties</span>
        </div>
        <div className="divide-y divide-oc-border">
          {items.map((d) => (
            <div key={d.code} data-testid={`row-${d.code}`} className="grid grid-cols-12 px-6 py-4 items-center text-[14px] gap-2 hover:bg-oc-subtle/40">
              <button
                type="button"
                onClick={() => copy(d.code)}
                className="col-span-3 font-mono font-semibold text-oc-primary text-left hover:text-oc-secondary"
                title="Kopieer code"
              >
                <Tag className="inline w-4 h-4 mr-1" />{d.code}
              </button>
              <span className="col-span-3 text-oc-textSecondary truncate">{d.email}</span>
              <span className="col-span-1 font-mono">{d.percent_off}%</span>
              <span className="col-span-2 text-oc-textSecondary text-[13px]">{d.valid_until_iso?.slice(0, 10) || "—"}</span>
              <span className="col-span-2 flex flex-col gap-1">
                <StatusBadge doc={d} />
                <span className="text-[10px] uppercase tracking-wide text-oc-textSecondary">{d.source}</span>
              </span>
              <span className="col-span-1 text-right">
                {!d.used && !d.revoked && (
                  <Button
                    data-testid={`revoke-${d.code}`}
                    onClick={() => revoke(d.code)}
                    variant="ghost"
                    size="sm"
                    className="text-oc-excessive hover:text-oc-excessive hover:bg-oc-excessive/10"
                    title="Code intrekken"
                  >
                    <Ban className="w-4 h-4" />
                  </Button>
                )}
              </span>
            </div>
          ))}
          {items.length === 0 && <p className="px-7 py-10 text-center text-oc-textSecondary">Geen codes gevonden.</p>}
        </div>
      </div>
    </div>
  );
}
